export const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

export const getFormTypeColor = (formType: string) => {
    switch (formType) {
        case '10-K':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
        case '8-K':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
    }
}

/**
 * Extracts the report type from filing description, stopping at "report"
 * Examples:
 * - "Form 8-K - Current report - Item 5.02 Item 5.03 Item 9.01" -> "Current report"
 * - "Form 10-K - Annual report [Section 13 and 15(d), not S-K Item 405]" -> "Annual report"
 * @param description - The filing description string
 * @returns The extracted report type or null if not found
 */
export const extractReportType = (description?: string): string | null => {
    if (!description) return null

    // Split by " - " to handle the pattern: "Form X - Report Type - Items..."
    const parts = description.split(' - ').map((part) => part.trim())

    // If we have at least 2 parts, the second part should be the report type
    if (parts.length >= 2 && parts[1]) {
        let reportType = parts[1]

        // If there's another " - " separator, split at that point first
        if (reportType.includes(' - ')) {
            const splitResult = reportType.split(' - ')[0]
            if (splitResult) {
                reportType = splitResult
            }
        }

        // Extract only up to and including "report" (case-insensitive)
        // Find the index of "report" and extract everything up to and including it
        const reportIndex = reportType.toLowerCase().indexOf('report')
        if (reportIndex !== -1) {
            const upToReport = reportType.substring(0, reportIndex + 'report'.length)
            return upToReport.trim()
        }

        // Fallback: if "report" not found but there are brackets, extract up to first bracket
        const bracketIndex = reportType.indexOf('[')
        if (bracketIndex !== -1) {
            return reportType.substring(0, bracketIndex).trim()
        }

        // Fallback: return the whole second part if no "report" found (less common case)
        return reportType
    }

    return null
}

import type { CompanyFilingView } from '@/services/filings/types'

/**
 * Groups filings by year and returns them sorted by year (descending)
 * Assumes filings are already sorted by filed_at (descending) from the service/API
 *
 * @param filings - Array of filings (should be pre-sorted by filed_at descending)
 * @returns Object with grouped filings by year and sorted year keys
 */
export interface GroupedFilingsByYear {
    groups: Record<number, CompanyFilingView[]>
    yearsDesc: number[]
}

export function groupFilingsByYear(filings: CompanyFilingView[]): GroupedFilingsByYear {
    const groups: Record<number, CompanyFilingView[]> = {}

    // Group filings by year
    // Since filings are pre-sorted by date (descending), we maintain that order within each year
    filings.forEach((filing) => {
        const year = new Date(filing.filed_at).getFullYear()
        if (!groups[year]) {
            groups[year] = []
        }
        groups[year].push(filing)
    })

    // Extract and sort years in descending order
    const yearsDesc = Object.keys(groups)
        .map(Number)
        .sort((a, b) => b - a)

    return { groups, yearsDesc }
}

/**
 * Groups filings by year based on period_of_report date
 * Used for 10-K and 10-Q views that need quarterly/annual grouping
 *
 * @param filings - Array of filings
 * @returns Object with grouped filings by period of report year and sorted year keys
 */
export function groupFilingsByPeriodOfReport(filings: CompanyFilingView[]): GroupedFilingsByYear {
    const groups: Record<number, CompanyFilingView[]> = {}

    // Group filings by period_of_report year
    filings.forEach((filing) => {
        if (!filing.filed_at) return

        const year = new Date(filing.filed_at).getFullYear()
        if (!groups[year]) {
            groups[year] = []
        }
        groups[year].push(filing)
    })

    // Sort filings within each year by period_of_report ascending (Q1, Q2, Q3, Q4)
    Object.keys(groups).forEach((yearStr) => {
        const year = Number(yearStr)
        const yearGroup = groups[year]
        if (yearGroup) {
            yearGroup.sort((a, b) => {
                const dateA = new Date(a.filed_at || 0).getTime()
                const dateB = new Date(b.filed_at || 0).getTime()
                return dateA - dateB
            })
        }
    })

    // Extract and sort years in descending order
    const yearsDesc = Object.keys(groups)
        .map(Number)
        .sort((a, b) => b - a)

    return { groups, yearsDesc }
}
